import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  Paper,
} from '@mui/material';
import {
  Key as KeyIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Category as CategoryIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { ChatSuggestion } from '../../types/unifiedSchema';

interface ChatSuggestionCardProps {
  suggestion: ChatSuggestion;
  onAccept: () => void;
  onReject: () => void;
}

const typeConfig: Record<string, { icon: React.ReactElement; label: string; color: string }> = {
  set_pk: { icon: <KeyIcon fontSize="small" />, label: 'Set Primary Key', color: '#4caf50' },
  add_relationship: { icon: <LinkIcon fontSize="small" />, label: 'Add Relationship', color: '#2196f3' },
  update_column: { icon: <EditIcon fontSize="small" />, label: 'Update Column', color: '#ff9800' },
  set_entity_type: { icon: <CategoryIcon fontSize="small" />, label: 'Set Entity Type', color: '#9c27b0' },
};

const ChatSuggestionCard: React.FC<ChatSuggestionCardProps> = ({
  suggestion,
  onAccept,
  onReject,
}) => {
  const config = typeConfig[suggestion.type] || {
    icon: <EditIcon fontSize="small" />,
    label: suggestion.type,
    color: '#757575',
  };

  const isResolved = suggestion.status !== 'pending';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        bgcolor: isResolved ? 'grey.50' : 'background.paper',
        opacity: isResolved ? 0.7 : 1,
        borderColor: isResolved
          ? suggestion.status === 'accepted'
            ? 'success.light'
            : 'error.light'
          : 'divider',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Box sx={{ color: config.color }}>{config.icon}</Box>
        <Typography variant="caption" fontWeight="medium" sx={{ flex: 1 }}>
          {config.label}
        </Typography>
        {isResolved && (
          <Chip
            label={suggestion.status === 'accepted' ? 'Applied' : 'Rejected'}
            size="small"
            color={suggestion.status === 'accepted' ? 'success' : 'default'}
            variant="outlined"
          />
        )}
      </Stack>

      <Typography variant="body2" sx={{ mb: 1, fontSize: '0.8rem' }}>
        {suggestion.description}
      </Typography>

      {!isResolved && (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<CheckIcon />}
            onClick={onAccept}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Accept
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<CloseIcon />}
            onClick={onReject}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Reject
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

export default ChatSuggestionCard;
